import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { forgotPassword } from "@/api/endpoints";
import { usePageTitle } from "@/hooks/usePageTitle";

export function ForgotPassword(): JSX.Element {
  usePageTitle("Reset your password");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [pending, setPending] = useState(false);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setPending(true);
    try {
      await forgotPassword(email);
    } catch {
      // Deliberately swallowed: the server answers identically whether or
      // not the account exists, and so do we — no enumeration via the UI.
    } finally {
      setSent(true);
      setPending(false);
    }
  }

  return (
    <main className="min-h-full flex items-center justify-center px-4">
      <form
        onSubmit={(e) => void onSubmit(e)}
        className="card w-full max-w-sm space-y-4"
        noValidate
      >
        <header className="space-y-1">
          <h1 className="text-xl font-semibold">Reset your password</h1>
          <p className="text-sm text-ink-300">
            Enter your account email and we&apos;ll send you a reset link.
          </p>
        </header>

        {sent ? (
          <div className="space-y-4">
            <p className="text-sm text-ink-200" role="status">
              If an account exists for <span className="font-medium">{email}</span>,
              a reset link is on its way. The link works once and expires in
              about an hour — check your spam folder if it doesn&apos;t arrive.
            </p>
            <Link to="/login" className="btn-primary w-full">
              Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <label className="block space-y-1">
              <span className="text-sm text-ink-200">Email</span>
              <input
                type="email"
                autoComplete="email"
                required
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>

            <button type="submit" className="btn-primary w-full" disabled={pending || !email}>
              {pending ? "Sending…" : "Send reset link"}
            </button>
            <p className="text-sm text-ink-400 text-center">
              <Link to="/login" className="hover:text-ink-100">
                Back to sign in
              </Link>
            </p>
          </>
        )}
      </form>
    </main>
  );
}
